import { useState, useEffect, lazy, Suspense } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'
import { TickerBar } from './TickerBar'
import { MobileNav } from './MobileNav'
import { Disclaimer } from './Disclaimer'
import { CommandPalette } from '../CommandPalette'
import { Breadcrumb } from '../ui/Breadcrumb'
import { ShortcutsModal } from '../ui/ShortcutsModal'
import { PastDueBanner, CancelAtPeriodEndBanner, TrialBanner } from '../billing'
// TEMP: GlobalAIBar disabled pending auth/session debug — see ideas-backlog
// import { GlobalAIBar } from '../ai/GlobalAIBar'
import { useUIStore } from '../../stores/uiStore'
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts'
import { useIsDesktop } from '../../hooks/useBreakpoint'
import clsx from 'clsx'
import './AppShell.css'

// Lazy load AI Panel for better initial load
const AIPanel = lazy(() => import('../ai/AIPanel').then(m => ({ default: m.AIPanel })))
const MobileAIOverlay = lazy(() => import('../ai/MobileAIOverlay').then(m => ({ default: m.MobileAIOverlay })))

// Data-dense pages where AI panel should auto-close
const DATA_DENSE_PAGES = ['/screener', '/options', '/charting', '/advanced-charting', '/backtester']

export function AppShell() {
  const location = useLocation()
  const isDesktop = useIsDesktop()
  const { sidebarCollapsed, aiPanelOpen, toggleAIPanel, setAIPanelOpen, aiPanelPrompt, clearAIPrompt, aiPanelManuallyOpened } = useUIStore()
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false)

  // Keyboard shortcut: Ctrl+. or Cmd+. to toggle AI panel
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '.') {
        e.preventDefault()
        toggleAIPanel()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [toggleAIPanel])

  // Auto-close AI panel on data-dense pages (unless manually opened)
  useEffect(() => {
    const isDataDensePage = DATA_DENSE_PAGES.some(p => location.pathname.includes(p))
    if (isDataDensePage && aiPanelOpen && !aiPanelManuallyOpened) {
      setAIPanelOpen(false)
    }
  }, [location.pathname, aiPanelOpen, aiPanelManuallyOpened, setAIPanelOpen])

  useKeyboardShortcuts({
    onCommandPalette: () => setCommandPaletteOpen(true),
    onEscape: () => setCommandPaletteOpen(false)
  })

  return (
    <div className={clsx(
      'app-shell',
      sidebarCollapsed && 'app-shell-collapsed',
      aiPanelOpen && isDesktop && 'app-shell-ai-open'
    )}>
      {/* Billing status banners - shown at very top */}
      <TrialBanner />
      <PastDueBanner />
      <CancelAtPeriodEndBanner />
      <TickerBar />

      <div className="app-shell-body">
        {/* Sidebar - only visible on desktop */}
        <Sidebar />

        <div className="app-main">
          <TopBar />
          <div className="app-breadcrumb-container">
            <Breadcrumb />
          </div>
          {/* TEMP: GlobalAIBar disabled pending auth/session debug — see ideas-backlog */}
          {/* <GlobalAIBar /> */}
          <main id="main-content" role="main" className="app-content">
            <Outlet />
            <Disclaimer />
          </main>
        </div>

        {/* Desktop AI Panel - only visible on desktop */}
        {isDesktop && (
          <Suspense fallback={null}>
            <AIPanel
              isOpen={aiPanelOpen}
              onToggle={toggleAIPanel}
              externalPrompt={aiPanelPrompt || undefined}
              onExternalPromptHandled={clearAIPrompt}
            />
          </Suspense>
        )}
      </div>

      {/* Mobile Navigation - only visible on tablet/mobile */}
      <MobileNav onSearchClick={() => setCommandPaletteOpen(true)} />

      {/* Mobile AI Overlay - only visible on tablet/mobile */}
      {!isDesktop && (
        <Suspense fallback={null}>
          <MobileAIOverlay />
        </Suspense>
      )}

      <CommandPalette
        isOpen={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
      />
      <ShortcutsModal />
    </div>
  )
}
